import type React from 'react';
import { cx } from '@/styled-system/css';
import { Button } from '../Button';
import { CloseIcon } from '../icons';
import { buttonBase, buttonVariants, iconWrapper } from '../Button/button.styles';
import { floatingCloseButton } from './closeButton.styles';

type CloseButtonVariant = 'standard' | 'floating';

type CloseButtonBaseProps = {
  variant?: CloseButtonVariant;
  'aria-label'?: string;
  disabled?: boolean;
  size?: 'sm' | 'md' | 'lg';
};

export type CloseButtonProps = CloseButtonBaseProps & {
  href?: string;
  onClick?: ((e: React.MouseEvent<HTMLAnchorElement>) => void) | (() => void);
  className?: string;
};

export const CloseButton = ({
  onClick,
  variant = 'standard',
  'aria-label': ariaLabel = 'Close',
  disabled = false,
  size = 'md',
  className,
  href,
}: CloseButtonProps) => {
  const floatingStyles = variant === 'floating' ? floatingCloseButton : '';
  const combinedClassName = cx(floatingStyles, className);

  // Render as anchor tag when href is provided (SEO crawlable)
  if (href !== undefined) {
    const anchorClasses = cx(
      buttonBase,
      buttonVariants({ shape: 'circle', tone: 'neutral', emphasis: 'outlined', size }),
      combinedClassName,
    );

    return (
      <a
        href={href}
        onClick={onClick as (e: React.MouseEvent<HTMLAnchorElement>) => void}
        className={anchorClasses}
        aria-label={ariaLabel}
        style={{ textDecoration: 'none' }}
      >
        <span className={iconWrapper} aria-hidden="true">
          <CloseIcon />
        </span>
      </a>
    );
  }

  return (
    <Button
      shape="circle"
      tone="neutral"
      emphasis="outlined"
      size={size}
      onClick={onClick as () => void}
      aria-label={ariaLabel}
      disabled={disabled}
      className={combinedClassName}
    >
      <CloseIcon />
    </Button>
  );
};
